import { isToday, isYesterday, isTomorrow } from "date-fns";
import { forwardRef } from "react";
import { GlassDatePicker } from "../../../../components/utils/GlassDatePicker";
import styles from "./AgendaDateSelector.module.css";

const CalendarIcon = () => (
  <svg className={styles.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 10h18M8 3v4M16 3v4" />
  </svg>
);

interface DateTriggerProps {
  /** Injected by react-datepicker: the date formatted with `dateFormat`. */
  value?: string;
  onClick?: () => void;
  /** Shown instead of `value` for the days around today ("Hier", "Demain"...). */
  relativeLabel?: string;
}

/**
 * react-datepicker clones its `customInput` and hands it `value` / `onClick`
 * (plus a ref), hence the forwardRef. Props the picker does not inject — like
 * `relativeLabel` — survive the clone.
 */
const DateTrigger = forwardRef<HTMLButtonElement, DateTriggerProps>(({ value, onClick, relativeLabel }, ref) => (
  <button ref={ref} type="button" className={styles.trigger} onClick={onClick}>
    <CalendarIcon />
    <span className={styles.label}>{relativeLabel ?? value}</span>
  </button>
));

export interface AgendaDateSelectorProps {
  date: Date;
  onChange: (date: Date) => void;
}

/**
 * Day selector shown above the events agenda. The "Aujourd'hui" shortcut only
 * appears once the browsed day is no longer the current one.
 */
export const AgendaDateSelector = ({ date, onChange }: AgendaDateSelectorProps) => {
  const today = isToday(date);
  // Relative wording reads better than the raw date for the days around today.
  const relative = today ? "Aujourd'hui" : isYesterday(date) ? "Hier" : isTomorrow(date) ? "Demain" : undefined;

  return (
    <div className={styles.container}>
      <GlassDatePicker
        selected={date}
        onChange={(d) => {
          if (d) onChange(d);
        }}
        dateFormat="EEEE d MMMM yyyy"
        customInput={<DateTrigger relativeLabel={relative} />}
      />
      {!today ? (
        <button type="button" className={styles.todayButton} onClick={() => { onChange(new Date()); }}>
          Aujourd'hui
        </button>
      ) : null}
    </div>
  );
};
